import { useQuery } from "@apollo/client";
import "components/Comments/CommentList";
import { gql } from "generated-graphql";
import "hooks/usePayment";
import { useParams, useSearchParams } from "react-router-dom";

export const FACILITY_REPORT_DETAILS_FRAGMENT = gql(`
  fragment FacilityReportDetailsFragment on Facility {
      id
      name
      county
      companyName
      naicsCode
      customerFacilityId
      tenantId
      tenant {
        id
        name
      }
      stateFields {
        key
        value
        type
        jurisdiction
        inputType
        label
        tooltip
        selectOptions {
          label
          value
        }
      }
      reportsManually
      city
      state
      streetAddress1
      streetAddress2
      zip
      mailingStreetAddress1
      mailingStreetAddress2
      mailingCity
      mailingState
      mailingZip
      mailingCountry
      portalCredentials {
        ...PortalCredentialFragment
      }
      jobs {
        id
        action
        status
        createdAt
        updatedAt
        logs {
          id
          jobId
          createdAt
          status
          errorMessage
          cloudWatchLogStream
          screenshotKey
          screenshotUrl
          updatedAt
        }
        stepsDone {
          id
          jobId
          name
          createdAt
          updatedAt
        }
      }
      contacts {
        id
        reportingRoles
        person {
          ...PersonFragment
        }
      }
      facilityAlternateIds {
        id
        type
        value
        hasNoId
      }
      lepc {
        id
        name
        email
        mailingAddress
        mailingAddressLine2
        city
        state
        county
        zip
        isEmailed
        isMailed
        phone
        additionalComments
        needsT2sFile
        isStateSubmits
      }
      supplementalLepcs {
        id
        lepc {
          id
          name
          email
          mailingAddress
          mailingAddressLine2
          city
          state
          county
          zip
          isEmailed
          isMailed
          phone
          additionalComments
          needsT2sFile
          isStateSubmits
        }
      }
      fireDepartment {
        id
        fireDeptId
        name
        email
        mailingAddress
        mailingAddressLine2
        city
        state
        county
        zip
        isMailed
        isEmailed
        phone
        phoneExt
        additionalComments
        stateSubmitsToFireDept
        needsT2sFile
      }
      supplementalFireDepartments {
        id
        fireDepartment {
          id
          fireDeptId
          name
          email
          mailingAddress
          mailingAddressLine2
          city
          state
          county
          zip
          isMailed
          isEmailed
          phone
          phoneExt
          additionalComments
          stateSubmitsToFireDept
          needsT2sFile
        }
      }
  }
`);

export const PERSON_FRAGMENT = gql(`
  fragment PersonFragment on Person {
    id
    first
    last
    email
    tenantId
    title
    city
    state
    streetAddress1
    zip
    phones {
      number
      type
    }
  }
`);

export const PORTAL_CREDENTIAL_FRAGMENT = gql(
  `
  fragment PortalCredentialFragment on PortalCredential {
    type
    tech
    url
    credential {
      id
      isEncampOwned
      kind
      password
      securityQuestions {
        question
        answer
      }
      username
      lastVerificationCheckAt
      lastVerificationSuccessAt
      verificationStatus
      tenantId
      usesCombinedInvoicing
      usesRcoCredential
    }
  }
`
);

export const GET_TIERII_REPORT = gql(`
query GetTierIIReport($reportId: ID!, $version: String) {
  tierIIReport(reportId: $reportId, version: $version) {
    id
    tenantId
    reportingYear
    reportKind
    productCount
    organizationStatus
    encampStatus
    tags
    filedOn
    portalReportId
    reasonForReport
    fulfillmentNote
    needsRevision
    needsRevisionReason
    issues {
      key
    }
    tenant {
      id
      name
      partnerTenantId
      partnerTenant {
        id
        name
      }
      contacts {
        id
        type
        firstName
        lastName
        email
        user {
          person {
            first
            last
          }
        }
      }
    }
    reviewers {
      isComplete
    }
   facility {
    ...FacilityReportDetailsFragment
   }
    cohortMemberships {
      id
      cohort {
        id
        type
        status
        allowsMultipleCohorts
        reportingYear
        state
        tenantId
        partnerTenantId
        fdId
        lepcId
        credentialUsername
        alternateIdValue
        members {
          id
          status
        }
        currentWorkflow {
          id
          status
          currentActivity {
            id
            type
          }
        }
      }
    }
    currentWorkflow {
      id
      activities {
        id
        type
        status
        executionPlan
        description
        message
        championId
        nonChampionIds
        cohortFacilityIds
        reportDetailsExtended
        output
        inputs {
          id
          activityId
          label
          shortName
          value
          required
          type
        }
        documents {
          document {
            id
            title
            description
            storageLink
            documentType
            updatedAt
            createdAt
            sizeMegabytes
          }
        }
        currentJob {
          id
          status
          action
        }
        payments {
          id
          status
          paidAt
          tenantId
          amount
          lobMail {
            id
            lobId
            status
            type
          }
        }
      }
      tags {
        id
        name
      }
      currentActivityId
      currentActivity {
        id
        type
        status
        executionPlan
        description
        message
        assigneeId
        currentJobId
        nonChampionIds
        currentJob {
          id
          taskArn
          taskUrl
        }
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        documents  {
          document {
            id
            title
            description
            storageLink
            documentType
            updatedAt
            createdAt
          }
        }
      }
    }
    documents {
      id
      title
      description
      storageLink
      documentType
      updatedAt
      createdAt
      activities {
        id
        type
        description
      }
    }
    verifierFullName
    verifierTitle
    verifierPhone
    verifierEmail
    verifiedAt
    comments {
      ...CommentList
    }
    payments {
      ...PaymentRow
    }
  }
}`);

export function useReportDetails() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version");

  const query = useQuery(GET_TIERII_REPORT, {
    variables: {
      reportId: reportId ?? "",
      // Transitional argument to do side-by-side-testing.
      version: version ?? undefined,
    },
    skip: !reportId,
  });

  if (query.error) throw query.error;

  return query;
}
